import React from 'react';
import { iconPlus, iconPlusWhite } from 'assets/icons';
import { Card } from 'components/Card';
import { InputFormField } from 'components/InputFormField';
import { CardContent, Container } from './styles';
import { useDepartmentContacts } from './useDepartmentContacts';
import { ResponsibleInputsFields } from './responsibleInputsFields';
import { Loading } from 'components/Loading';
import { PlaceHolderNotExists } from 'components/PlaceholderNotExists';
import { Footer } from 'components/Footer';
import { Title } from 'components/Title';
import { Badge } from 'components/Badge';
import { Button } from 'components/Button';
import { variablePhoneMask } from 'utils/masks';

export const DepartmentContacts: React.FC = () => {
  const {
    isLoading,
    hasError,
    inputsValues,
    errors,
    touched,
    changedFields,
    encryptedFields,
    translatedFields,
    handleChange,
    handleSubmit,
    handleAddResponsible,
    handleRemoveResponsible,
    handleFormSubmit,
    handleFormDiscard,
    handleDecrypt,
    initialInputsValues,
  } = useDepartmentContacts();

  if (isLoading) {
    return <Loading/>
  }

  if (hasError) {
    return (
      <PlaceHolderNotExists title="Erro ao tentar conectar com o servidor" />
    )
  }

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <Card title="Responsável Comercial">
          <CardContent>
            <InputFormField
              name="commercial.responsible"
              label="Nome completo"
              marginBotton="0"
              value={inputsValues.commercial.responsible}
              onChange={handleChange}
              block={true}
              error={touched.commercial?.responsible && !!errors.commercial?.responsible}
              mensageError="Nome inválido"
            />
            <InputFormField
              name="commercial.email"
              label="E-mail comercial"
              marginBotton="0"
              value={inputsValues.commercial.email}
              onChange={handleChange}
              block={true}
              isEncrypted={true}
              error={touched.commercial?.email && !!errors.commercial?.email}
              mensageError="E-mail inválido"
              currentStateEncrypted={inputsValues.commercial.email ? encryptedFields.commercial.email.isEncrypted : false}
              readOnly={!!initialInputsValues.commercial.email?.length && initialInputsValues.commercial.email === inputsValues.commercial.email && encryptedFields.commercial.email.isEncrypted}
              animation={encryptedFields.commercial.email.isLoading}
              onClick={() => handleDecrypt('commercial', 'email')}
            />
            <InputFormField
              name="commercial.phoneNumber"
              label="Telefone"
              marginBotton="0"
              value={inputsValues.commercial.phoneNumber}
              onChange={handleChange}
              block={true}
              isEncrypted={!!initialInputsValues.commercial.phoneNumber}
              mask={variablePhoneMask}
              error={touched.commercial?.phoneNumber && !!errors.commercial?.phoneNumber}
              mensageError="Telefone inválido"
              currentStateEncrypted={inputsValues.commercial.phoneNumber ? encryptedFields.commercial.phoneNumber.isEncrypted : false}
              readOnly={!!initialInputsValues.commercial.phoneNumber?.length && initialInputsValues.commercial.phoneNumber === inputsValues.commercial.phoneNumber && encryptedFields.commercial.phoneNumber.isEncrypted}
              animation={encryptedFields.commercial.phoneNumber.isLoading}
              onClick={() => handleDecrypt('commercial', 'phoneNumber')}
            />
            <InputFormField
              name="commercial.branch"
              label="Ramal"
              marginBotton="0"
              value={inputsValues.commercial.branch?.toString()}
              onChange={handleChange}
              block={true}
              error={touched.commercial?.branch && !!errors.commercial?.branch}
              mensageError="Ramal inválido"
            />
          </CardContent>
        </Card>
        
        <Card 
          name="sac"
          title="Responsável pelo SAC" 
          labelButton="Cadastrar" 
          onClick={handleAddResponsible}
          iconButton={iconPlus} 
          iconHover={iconPlusWhite}
          hasButton={Object.keys(inputsValues.sac).length === 0}
          showContent={Object.keys(inputsValues.sac).length > 0}
        >
          <ResponsibleInputsFields
            name="sac"
            inputsValues={inputsValues.sac}
            errors={errors.sac}
            touched={touched.sac}
            handleChange={handleChange}
            handleRemoveResponsible={handleRemoveResponsible}
            encryptedFields={encryptedFields}
            handleDecrypt={handleDecrypt}
          />
        </Card>

        <Card 
          name="finances"
          title="Responsável Financeiro" 
          labelButton="Cadastrar" 
          iconButton={iconPlus} 
          iconHover={iconPlusWhite}
          onClick={handleAddResponsible}
          hasButton={Object.keys(inputsValues.finances).length === 0}
          showContent={Object.keys(inputsValues.finances).length > 0}
        >
          <ResponsibleInputsFields
            name="finances"
            inputsValues={inputsValues.finances}
            errors={errors.finances}
            touched={touched.finances}
            handleChange={handleChange}
            handleRemoveResponsible={handleRemoveResponsible}
            encryptedFields={encryptedFields}
            handleDecrypt={handleDecrypt}
          />
        </Card>

        <Card 
          name="legal"
          title="Responsável Jurídico" 
          labelButton="Cadastrar" 
          iconButton={iconPlus} 
          iconHover={iconPlusWhite}
          onClick={handleAddResponsible}
          hasButton={Object.keys(inputsValues.legal).length === 0}
          showContent={Object.keys(inputsValues.legal).length > 0}
        >
          <ResponsibleInputsFields
            name="legal"
            inputsValues={inputsValues.legal}
            errors={errors.legal}
            touched={touched.legal}
            handleChange={handleChange}
            handleRemoveResponsible={handleRemoveResponsible}
            encryptedFields={encryptedFields}
            handleDecrypt={handleDecrypt}
          />
        </Card>

        <Card 
          name="it"
          title="Responsável TI" 
          labelButton="Cadastrar" 
          iconButton={iconPlus} 
          iconHover={iconPlusWhite}
          onClick={handleAddResponsible}
          hasButton={Object.keys(inputsValues.it).length === 0}
          showContent={Object.keys(inputsValues.it).length > 0}
        >
          <ResponsibleInputsFields
            name="it"
            inputsValues={inputsValues.it}
            errors={errors.it}
            touched={touched.it}
            handleChange={handleChange}
            handleRemoveResponsible={handleRemoveResponsible}
            encryptedFields={encryptedFields}
            handleDecrypt={handleDecrypt}
          />
        </Card>

        <Footer>
          <Title elementHTML="strong" text="Campos atualizados:" className="tag"/>
          {changedFields.map((item: string) => (
            item ?
              <Badge value={translatedFields(item) ?? ''} key={item}/> : null
          ))}

          <Button 
            label="Cancelar" 
            typeBtn="outline"
            type="button"
            onClick={handleFormDiscard} 
          />
          <Button 
            label="Atualizar dados" 
            typeBtn="blue" 
            type="button"
            onClick={handleFormSubmit}
          />
        </Footer>
      </form>
    </Container>
  );
}
